import { Injectable, inject } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import {
  ActionPerformed,
  CancelOptions,
  LocalNotificationDescriptor,
  LocalNotifications,
  LocalNotificationSchema,
  ScheduleOptions,
} from '@capacitor/local-notifications';
import { AnalyticsService } from '../analytics/analytics.service';
import { EventName } from '../analytics/event-names';
import { ProfileService } from 'src/app/core/services/profile/profile.service';
import { toObservable } from '@angular/core/rxjs-interop';
import { TranslocoService } from '@jsverse/transloco';

enum NotificationType {
  RetentionHrs8 = 1,
  RetentionHrs24,
  RetentionHrs48,
  RetentionHrs72,
  RetentionHrs96,
  RetentionHrs120,
  RetentionHrs144,
  RetentionHrs168,
  RetentionHrs336,
  RetentionHrs504,
  RetentionHrs672,
  RetentionHrs840,
  RetentionHrs1008,
  RetentionHrs1176,
  RetentionHrs1344,
  RetentionHrs1512,
  RetentionHrs1680,
  RetentionHrs1848,
  RetentionHrs2016,
  RetentionHrs2184,
  RetentionHrs2352,

  OnboardingMins10,
  OnboardingMins60,
  OnboardingHrs8,
  OnboardingHrs24,
  OnboardingHrs48,
  OnboardingHrs72,
  OnboardingHrs96,
  OnboardingHrs120,
  OnboardingHrs144,
  OnboardingHrs168,
  OnboardingHrs336,
}

export interface ObjectNode {
  [key: string]: Node;
}
export type Node = ObjectNode | string | number | boolean;

interface NotificationData {
  id: NotificationType;
  title: string;
  body: string;
  intervalMins: number;
  extra: ObjectNode;
}

interface NotificationMetadata {
  type: string;
  intervalMins: number;
}

@Injectable({
  providedIn: 'root',
})
export class LocalMessagingService {
  private analyticsService = inject(AnalyticsService);
  private profileService = inject(ProfileService);
  private translocoService = inject(TranslocoService);

  private profileInitialised: boolean | undefined = false;

  constructor() {
    toObservable(this.profileService.initialised).subscribe((initialised) => {
      this.profileInitialised = initialised;
      this.recordOnboardingStateChange();
    });
    toObservable(this.profileService.nickname).subscribe((_) => {
      if (this.profileInitialised) {
        this.recordOnboardingStateChange();
      }
    });
    toObservable(this.profileService.defaultWallet).subscribe((_) => {
      if (this.profileInitialised) {
        this.recordOnboardingStateChange();
      }
    });

    LocalNotifications.addListener(
      'localNotificationReceived',
      (notification: LocalNotificationSchema) => {
        console.log(
          `LocalNotifications localNotificationReceived: ${JSON.stringify(notification)}`,
        );
      },
    );

    LocalNotifications.addListener(
      'localNotificationActionPerformed',
      (notificationAction: ActionPerformed) => {
        if (notificationAction.actionId === 'tap') {
          const metadata = notificationAction.notification.extra as NotificationMetadata;
          this.analyticsService.log(EventName.LocalNotificationOpenAction, metadata);
        }
        console.log(
          `LocalNotifications localNotificationActionPerformed: ${JSON.stringify(
            notificationAction,
          )}`,
        );
      },
    );
  }

  async requestPermissions(): Promise<void> {
    try {
      if (!Capacitor.isNativePlatform()) {
        return;
      }
      const permissionStatus = await LocalNotifications.requestPermissions();
      this.analyticsService.log(EventName.LocalNotificationPermissions, {
        status: permissionStatus.display,
      });
    } catch (error) {
      this.analyticsService.handleError(error, 'Request local notification permissions');
    }
  }

  async recordOnboardingStateChange(): Promise<void> {
    if (!Capacitor.isNativePlatform()) {
      return;
    }

    await this.cancelNotifications(LocalMessagingService.onboardingNotificationIds());

    if (!this.profileInitialised) {
      return;
    }

    const defaultWallet = this.profileService.defaultWallet();
    const nickname = this.profileService.nickname();

    if (!defaultWallet) {
      // Not saved any default wallet yet
      await this.scheduleNotifications(
        LocalMessagingService.onboardingNotifications(
          this.translocoService.translate('local-notification.onboarding.title-2'),
          this.translocoService.translate('local-notification.onboarding.message-2'),
        ),
      );
      return;
    }
    if (!nickname) {
      // Not save nickname
      await this.scheduleNotifications(
        LocalMessagingService.onboardingNotifications(
          this.translocoService.translate('local-notification.onboarding.title-1'),
          this.translocoService.translate('local-notification.onboarding.message-1'),
        ),
      );
      return;
    }
  }

  async recordProfileCompleted(): Promise<void> {
    if (!Capacitor.isNativePlatform()) {
      return;
    }

    await this.cancelNotifications(LocalMessagingService.onboardingNotificationIds());

    if (!this.profileInitialised) {
      return;
    }

    await this.scheduleNotifications(
      LocalMessagingService.onboardingNotifications(
        this.translocoService.translate('local-notification.onboarding.title-3'),
        this.translocoService.translate('local-notification.onboarding.message-3'),
      ),
    );
  }

  async clearOnboardingNotifications(): Promise<void> {
    if (!Capacitor.isNativePlatform()) {
      return;
    }
    await this.cancelNotifications(LocalMessagingService.onboardingNotificationIds());
  }

  async recordPayDrawViewed(): Promise<void> {
    if (!Capacitor.isNativePlatform()) {
      return;
    }

    await this.cancelNotifications(LocalMessagingService.onboardingNotificationIds());

    await this.scheduleNotifications(
      LocalMessagingService.onboardingNotifications(
        this.translocoService.translate('local-notification.onboarding.title-4'),
        this.translocoService.translate('local-notification.onboarding.message-4'),
      ),
    );
  }

  async recordGameComplete(): Promise<void> {
    if (!Capacitor.isNativePlatform()) {
      return;
    }

    const nickname = this.profileService.nickname();
    await this.cancelNotifications(LocalMessagingService.retentionNotificationIds());
    await this.scheduleNotifications(
      LocalMessagingService.retentionNotifications(nickname, this.translocoService),
    );
  }

  private async cancelNotifications(notificationIds: number[]): Promise<void> {
    try {
      const localNotifications: LocalNotificationDescriptor[] = notificationIds.map(
        (notificationId) => {
          return {
            id: notificationId,
          };
        },
      );

      const options: CancelOptions = {
        notifications: localNotifications,
      };
      await LocalNotifications.cancel(options);
    } catch (error) {
      this.analyticsService.handleError(error, 'Cancel location notifications');
    }
  }

  private async scheduleNotifications(notifications: NotificationData[]): Promise<void> {
    try {
      const permissionStatus = await LocalNotifications.checkPermissions();
      if (permissionStatus.display !== 'granted') {
        return;
      }

      const dateNow = Date.now();
      const localNotifications: LocalNotificationSchema[] = notifications.map((data) => {
        return {
          id: data.id,
          title: data.title,
          body: data.body,
          schedule: {
            at: new Date(dateNow + data.intervalMins * 60 * 1000),
          },
          extra: data.extra,
        };
      });

      const options: ScheduleOptions = {
        notifications: localNotifications,
      };
      await LocalNotifications.schedule(options);
    } catch (error) {
      this.analyticsService.handleError(error, 'Schedule local notifications');
    }
  }

  private static onboardingNotificationIds(): number[] {
    return [
      NotificationType.OnboardingMins10,
      NotificationType.OnboardingMins60,
      NotificationType.OnboardingHrs8,
      NotificationType.OnboardingHrs24,
      NotificationType.OnboardingHrs48,
      NotificationType.OnboardingHrs72,
      NotificationType.OnboardingHrs96,
      NotificationType.OnboardingHrs120,
      NotificationType.OnboardingHrs144,
      NotificationType.OnboardingHrs168,
      NotificationType.OnboardingHrs336,
    ];
  }

  private static onboardingNotifications(title: string, body: string): NotificationData[] {
    const onboardings = [
      {
        id: NotificationType.OnboardingMins10,
        onMinute: 10,
      },
      {
        id: NotificationType.OnboardingMins60,
        onMinute: 60,
      },
      {
        id: NotificationType.OnboardingHrs8,
        onMinute: 8 * 60,
      },
      {
        id: NotificationType.OnboardingHrs24,
        onMinute: 24 * 60,
      },
      {
        id: NotificationType.OnboardingHrs48,
        onMinute: 48 * 60,
      },
      {
        id: NotificationType.OnboardingHrs72,
        onMinute: 72 * 60,
      },
      {
        id: NotificationType.OnboardingHrs96,
        onMinute: 96 * 60,
      },
      {
        id: NotificationType.OnboardingHrs120,
        onMinute: 120 * 60,
      },
      {
        id: NotificationType.OnboardingHrs144,
        onMinute: 144 * 60,
      },
      {
        id: NotificationType.OnboardingHrs168,
        onMinute: 168 * 60,
      },
    ];

    return onboardings.map((onboarding) => {
      return {
        id: onboarding.id,
        title: title,
        body: body,
        intervalMins: onboarding.onMinute,
        extra: {
          event: {
            type: 'onboarding',
            intervalMins: onboarding.onMinute,
          },
        },
      };
    });
  }

  private static retentionNotificationIds(): number[] {
    return [
      NotificationType.RetentionHrs8,
      NotificationType.RetentionHrs24,
      NotificationType.RetentionHrs48,
      NotificationType.RetentionHrs72,
      NotificationType.RetentionHrs96,
      NotificationType.RetentionHrs120,
      NotificationType.RetentionHrs144,
      NotificationType.RetentionHrs168,
      NotificationType.RetentionHrs336,
      NotificationType.RetentionHrs504,
      NotificationType.RetentionHrs672,
      NotificationType.RetentionHrs840,
      NotificationType.RetentionHrs1008,
      NotificationType.RetentionHrs1176,
      NotificationType.RetentionHrs1344,
      NotificationType.RetentionHrs1512,
      NotificationType.RetentionHrs1680,
      NotificationType.RetentionHrs1848,
      NotificationType.RetentionHrs2016,
      NotificationType.RetentionHrs2184,
    ];
  }

  private static retentionNotifications(
    nickname: string | undefined,
    translocoService: TranslocoService,
  ): NotificationData[] {
    const retentions = [
      {
        id: NotificationType.RetentionHrs8,
        title: translocoService.translate('local-notification.retention.title-1'),
        body: translocoService.translate('local-notification.retention.message-1'),
        onHour: 8,
      },
      {
        id: NotificationType.RetentionHrs24,
        title: translocoService.translate('local-notification.retention.title-2'),
        body: translocoService.translate('local-notification.retention.message-2'),
        onHour: 24,
      },
      {
        id: NotificationType.RetentionHrs48,
        title: translocoService.translate('local-notification.retention.title-3'),
        body: translocoService.translate('local-notification.retention.message-3'),
        onHour: 48,
      },
      {
        id: NotificationType.RetentionHrs72,
        title: translocoService.translate('local-notification.retention.title-4'),
        body: translocoService.translate('local-notification.retention.message-4'),
        onHour: 72,
      },
      {
        id: NotificationType.RetentionHrs96,
        title: translocoService.translate('local-notification.retention.title-5'),
        body: translocoService.translate('local-notification.retention.message-5'),
        onHour: 96,
      },
      {
        id: NotificationType.RetentionHrs120,
        title: translocoService.translate('local-notification.retention.title-6'),
        body: translocoService.translate('local-notification.retention.message-6'),
        onHour: 120,
      },
      {
        id: NotificationType.RetentionHrs144,
        title: translocoService.translate('local-notification.retention.title-7'),
        body: translocoService.translate('local-notification.retention.message-7'),
        onHour: 144,
      },
      {
        id: NotificationType.RetentionHrs168,
        title: translocoService.translate('local-notification.retention.title-8'),
        body: translocoService.translate('local-notification.retention.message-8'),
        onHour: 168,
      },
      {
        id: NotificationType.RetentionHrs336,
        title: translocoService.translate('local-notification.retention.title-9'),
        body: nickname
          ? translocoService.translate('local-notification.retention.message-9', { nickname })
          : translocoService.translate('local-notification.retention.message-9-no-nick'),
        onHour: 336,
      },
      {
        id: NotificationType.RetentionHrs504,
        title: translocoService.translate('local-notification.retention.title-10'),
        body: translocoService.translate('local-notification.retention.message-10'),
        onHour: 504,
      },
      {
        id: NotificationType.RetentionHrs672,
        title: translocoService.translate('local-notification.retention.title-11'),
        body: translocoService.translate('local-notification.retention.message-11'),
        onHour: 672,
      },
      {
        id: NotificationType.RetentionHrs840,
        title: translocoService.translate('local-notification.retention.title-12'),
        body: translocoService.translate('local-notification.retention.message-12'),
        onHour: 840,
      },
      {
        id: NotificationType.RetentionHrs1008,
        title: translocoService.translate('local-notification.retention.title-13'),
        body: translocoService.translate('local-notification.retention.message-13'),
        onHour: 1008,
      },
      {
        id: NotificationType.RetentionHrs1176,
        title: translocoService.translate('local-notification.retention.title-14'),
        body: translocoService.translate('local-notification.retention.message-14'),
        onHour: 1176,
      },
      {
        id: NotificationType.RetentionHrs1344,
        title: translocoService.translate('local-notification.retention.title-15'),
        body: translocoService.translate('local-notification.retention.message-15'),
        onHour: 1344,
      },
      {
        id: NotificationType.RetentionHrs1512,
        title: translocoService.translate('local-notification.retention.title-16'),
        body: translocoService.translate('local-notification.retention.message-16'),
        onHour: 1512,
      },
      {
        id: NotificationType.RetentionHrs1680,
        title: translocoService.translate('local-notification.retention.title-17'),
        body: translocoService.translate('local-notification.retention.message-17'),
        onHour: 1680,
      },
      {
        id: NotificationType.RetentionHrs1848,
        title: translocoService.translate('local-notification.retention.title-18'),
        body: nickname
          ? translocoService.translate('local-notification.retention.message-18', { nickname })
          : translocoService.translate('local-notification.retention.message-18-no-nick'),
        onHour: 1848,
      },
      {
        id: NotificationType.RetentionHrs2016,
        title: translocoService.translate('local-notification.retention.title-19'),
        body: translocoService.translate('local-notification.retention.message-19'),
        onHour: 2016,
      },
    ];

    return retentions.map((retention) => {
      return {
        id: retention.id,
        title: retention.title,
        body: retention.body,
        intervalMins: retention.onHour * 60,
        extra: {
          event: {
            type: 'retention',
            intervalMins: retention.onHour * 60,
          },
        },
      };
    });
  }
}
